// src/components/wizard/StepInput.jsx - ステップ1: テーマ入力

import React, { useState } from 'react';
import { COLORS, SPACING, BORDER_RADIUS, TRANSITIONS } from '../../styles/constants';

const StepInput = ({
  prompt,
  setPrompt,
  tone,
  setTone,
  onGenerate,
  isLoading,
  userPlan,
  usage,
  onUpgrade
}) => {
  const [showTemplates, setShowTemplates] = useState(false);

  const tones = ['カジュアル', 'ビジネス', 'ユーモア', '丁寧'];

  const templates = [
    { category: 'ビジネス', items: ['今日の業務報告', '新サービスの告知', 'お客様への感謝'] },
    { category: '日常', items: ['今日のランチ', '週末の予定', '最近ハマってること'] },
    { category: 'イベント', items: ['セール告知', '新商品発売', 'キャンペーン情報'] },
    { category: 'tips', items: ['業界の裏話', '仕事のコツ', 'おすすめ紹介'] }
  ];

  const remainingPosts = userPlan === 'premium'
    ? '無制限'
    : `残り${Math.max(0, 3 - (usage?.count || 0))}回`;

  const handleTemplateSelect = (item) => {
    setPrompt(item);
    setShowTemplates(false);
  };

  return (
    <div style={{
      background: COLORS.surface,
      borderRadius: BORDER_RADIUS.xl,
      padding: SPACING.xl,
      border: `1px solid ${COLORS.border}`,
      animation: 'fadeIn 0.3s ease-out'
    }}>
      {/* ヘッダー */}
      <div style={{
        textAlign: 'center',
        marginBottom: SPACING.xl
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(16, 185, 129, 0.2))',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          marginBottom: SPACING.md,
          fontSize: '1.75rem'
        }}>
          ✏️
        </div>
        <h2 style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#fff',
          margin: 0,
          marginBottom: SPACING.xs
        }}>
          投稿したいテーマを入力
        </h2>
        <p style={{
          fontSize: '0.875rem',
          color: '#64748b',
          margin: 0
        }}>
          AIがあなたに代わって投稿文を作成します
        </p>
      </div>

      {/* 残り回数表示 */}
      <div style={{
        background: COLORS.background,
        borderRadius: BORDER_RADIUS.md,
        padding: SPACING.sm,
        marginBottom: SPACING.lg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: `1px solid ${COLORS.border}`
      }}>
        <span style={{
          fontSize: '0.8125rem',
          color: '#94a3b8'
        }}>
          本日の生成回数
        </span>
        <span style={{
          fontSize: '0.875rem',
          fontWeight: '600',
          color: remainingPosts === '無制限' ? COLORS.success : COLORS.primary
        }}>
          {remainingPosts}
        </span>
      </div>

      {/* テンプレートボタン */}
      <button
        onClick={() => setShowTemplates(!showTemplates)}
        style={{
          width: '100%',
          padding: SPACING.sm,
          background: showTemplates ? 'rgba(255,255,255,0.05)' : 'transparent',
          border: '1px dashed rgba(255,255,255,0.2)',
          borderRadius: BORDER_RADIUS.md,
          cursor: 'pointer',
          color: '#94a3b8',
          fontSize: '0.875rem',
          marginBottom: SPACING.md,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: SPACING.xs,
          transition: TRANSITIONS.default
        }}
      >
        <span>💡</span>
        テンプレートから選ぶ
        <span style={{
          transform: showTemplates ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: TRANSITIONS.default
        }}>▼</span>
      </button>

      {/* テンプレート一覧 */}
      {showTemplates && (
        <div style={{
          background: COLORS.background,
          borderRadius: BORDER_RADIUS.md,
          padding: SPACING.md,
          marginBottom: SPACING.md,
          animation: 'slideDown 0.2s ease-out',
          border: `1px solid ${COLORS.border}`
        }}>
          {templates.map((category) => (
            <div key={category.category} style={{ marginBottom: SPACING.sm }}>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#64748b',
                marginBottom: SPACING.xs
              }}>
                {category.category}
              </div>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.375rem'
              }}>
                {category.items.map((item) => (
                  <button
                    key={item}
                    onClick={() => handleTemplateSelect(item)}
                    style={{
                      padding: '0.375rem 0.75rem',
                      background: COLORS.surface,
                      border: `1px solid ${COLORS.border}`,
                      borderRadius: BORDER_RADIUS.sm,
                      cursor: 'pointer',
                      fontSize: '0.8125rem',
                      color: '#e2e8f0',
                      transition: TRANSITIONS.default
                    }}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* テーマ入力 */}
      <div style={{ marginBottom: SPACING.lg }}>
        <label style={{
          display: 'block',
          fontSize: '0.8125rem',
          fontWeight: '600',
          color: '#e2e8f0',
          marginBottom: SPACING.xs
        }}>
          テーマ・キーワード
        </label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="例: 新商品のカフェラテが美味しい"
          style={{
            width: '100%',
            minHeight: '100px',
            padding: SPACING.md,
            border: `1px solid ${COLORS.border}`,
            borderRadius: BORDER_RADIUS.md,
            fontSize: '1rem',
            resize: 'vertical',
            outline: 'none',
            transition: TRANSITIONS.default,
            boxSizing: 'border-box',
            background: COLORS.background,
            color: '#e2e8f0'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = COLORS.primary;
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'rgba(255,255,255,0.1)';
          }}
        />
        <div style={{
          fontSize: '0.75rem',
          color: '#64748b',
          marginTop: SPACING.xs,
          textAlign: 'right'
        }}>
          {prompt.length}/200
        </div>
      </div>

      {/* トーン選択 */}
      <div style={{ marginBottom: SPACING.xl }}>
        <label style={{
          display: 'block',
          fontSize: '0.8125rem',
          fontWeight: '600',
          color: '#e2e8f0',
          marginBottom: SPACING.sm
        }}>
          トーン（文体）
        </label>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: SPACING.sm
        }}>
          {tones.map((t) => (
            <button
              key={t}
              onClick={() => setTone(t)}
              style={{
                padding: SPACING.sm,
                background: tone === t ? COLORS.primary : COLORS.background,
                color: tone === t ? '#fff' : '#e2e8f0',
                border: tone === t ? 'none' : `1px solid ${COLORS.border}`,
                borderRadius: BORDER_RADIUS.md,
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                transition: TRANSITIONS.default
              }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* 生成ボタン */}
      <button
        onClick={onGenerate}
        disabled={isLoading || !prompt.trim()}
        style={{
          width: '100%',
          padding: '1rem',
          background: isLoading || !prompt.trim()
            ? 'rgba(255,255,255,0.1)'
            : COLORS.primary,
          color: isLoading || !prompt.trim() ? '#64748b' : '#fff',
          border: 'none',
          borderRadius: BORDER_RADIUS.md,
          cursor: isLoading || !prompt.trim() ? 'not-allowed' : 'pointer',
          fontSize: '1rem',
          fontWeight: '600',
          transition: TRANSITIONS.default,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: SPACING.sm
        }}
      >
        {isLoading ? (
          <>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid rgba(255,255,255,0.3)',
              borderTopColor: COLORS.white,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            生成中...
          </>
        ) : (
          <>
            <span>✨</span>
            投稿文を生成する
          </>
        )}
      </button>

      {/* アップグレード促進 */}
      {userPlan === 'free' && (
        <div style={{
          marginTop: SPACING.lg,
          padding: SPACING.md,
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(16, 185, 129, 0.1))',
          borderRadius: BORDER_RADIUS.xl,
          textAlign: 'center',
          border: '1px solid rgba(59, 130, 246, 0.3)'
        }}>
          <p style={{
            fontSize: '0.8125rem',
            color: '#e2e8f0',
            margin: 0,
            marginBottom: SPACING.sm
          }}>
            もっとたくさん投稿したい？
          </p>
          <button
            onClick={onUpgrade}
            style={{
              padding: '0.5rem 1.25rem',
              background: COLORS.primary,
              color: '#fff',
              border: 'none',
              borderRadius: BORDER_RADIUS.md,
              cursor: 'pointer',
              fontSize: '0.8125rem',
              fontWeight: '600'
            }}
          >
            プランを確認する →
          </button>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default StepInput;
